/**
 * Canonical game registry — one source of truth for the five games' display
 * metadata. Previously this lived scattered across `GAME_META` in
 * ProfilePage, the `GameIcon` switch, and the GamesGrid cards. The Stats
 * page drives its per-game switcher, accent theming and "Play now" CTAs off
 * this list, so it lives here once.
 *
 * `key` matches both `MatchEntry["source"]` (lib/pnl) and the API's
 * `GameKey` (sessionGamesStore) — i.e. "td", not "tower-defense". The
 * `icon` / `playView` fields carry the longer "tower-defense" spelling the
 * GameIcon component and the App router expect.
 */
import type { MatchEntry } from "./pnl";

export type GameKey = MatchEntry["source"]; // "pong" | "fps" | "snake" | "td" | "math"

/** The game-launch targets App.handlePlayGame understands. */
export type PlayTarget = "pong" | "fps" | "tower-defense" | "snake" | "math";

/** GameIcon's prop spelling. */
export type GameIconKey = "pong" | "fps" | "snake" | "tower-defense" | "math";

export interface GameMeta {
  key: GameKey;
  /** Full title for the switcher headline, e.g. "FPS Arena". */
  label: string;
  /** Compact label for chips / tabs, e.g. "FPS". */
  short: string;
  /** Accent hex — themes the whole page when this game is selected. */
  accent: string;
  /** Translucent tint of the accent, for chip backgrounds. */
  bg: string;
  /** GameIcon component key. */
  icon: GameIconKey;
  /** Where the "Play now" CTA sends the user. */
  playView: PlayTarget;
  /** Elo applies to head-to-head games only. Snake is single-player. */
  hasRating: boolean;
  /** One-line tagline shown under the headline / in empty states. */
  blurb: string;
}

/** Display order of the switcher carousel. */
export const GAMES: GameMeta[] = [
  {
    key: "fps",
    label: "FPS Arena",
    short: "FPS",
    accent: "#FF6B00",
    bg: "rgba(255,107,0,0.12)",
    icon: "fps",
    playView: "fps",
    hasRating: true,
    blurb: "Tactical 1v1 — outshoot your opponent, first to 7 rounds takes the pot.",
  },
  {
    key: "pong",
    label: "Pong",
    short: "Pong",
    accent: "#FF6B00",
    bg: "rgba(255,107,0,0.12)",
    icon: "pong",
    playView: "pong",
    hasRating: true,
    blurb: "Classic duel. Faster reflexes win the wager.",
  },
  {
    key: "snake",
    label: "Snake",
    short: "Snake",
    accent: "#3DDC97",
    bg: "rgba(61,220,151,0.12)",
    icon: "snake",
    playView: "snake",
    hasRating: false,
    blurb: "Grow, hunt, and cash out before you're eaten. Free-for-all arena.",
  },
  {
    key: "td",
    label: "Tower Defense",
    short: "Tower Defense",
    accent: "#60A5FA",
    bg: "rgba(96,165,250,0.12)",
    icon: "tower-defense",
    playView: "tower-defense",
    hasRating: true,
    blurb: "Outlast your rival's waves. Last tower standing wins.",
  },
  {
    key: "math",
    label: "Math Duel",
    short: "Math",
    accent: "#C084FC",
    bg: "rgba(192,132,252,0.12)",
    icon: "math",
    playView: "math",
    hasRating: true,
    blurb: "Solve faster than your opponent. Pure speed and accuracy.",
  },
];

export const GAME_KEYS: GameKey[] = GAMES.map((g) => g.key);

export const GAME_BY_KEY: Record<GameKey, GameMeta> = GAMES.reduce(
  (acc, g) => {
    acc[g.key] = g;
    return acc;
  },
  {} as Record<GameKey, GameMeta>,
);

/** Narrow an arbitrary string to a GameKey, or null. */
export function toGameKey(raw: string | null | undefined): GameKey | null {
  if (!raw) return null;
  return (GAME_KEYS as string[]).includes(raw) ? (raw as GameKey) : null;
}

/** Next / previous game in the carousel, wrapping around. */
export function stepGame(current: GameKey, dir: 1 | -1): GameKey {
  const i = GAME_KEYS.indexOf(current);
  const n = (i + dir + GAME_KEYS.length) % GAME_KEYS.length;
  return GAME_KEYS[n];
}
